/***
*
*   SIGNUP PROMOTIONAL DATA
*   View and analyze signup promotional data and company information
*
**********/

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card, Table, Input, Select, Space, Typography, Row, Col,
  Statistic, Empty, Tag, Button, Tooltip
} from 'antd';
import {
  UserAddOutlined, MessageOutlined, StarOutlined, TrophyOutlined,
  SearchOutlined, ReloadOutlined, BarChartOutlined, PieChartOutlined,
  MailOutlined, ShopOutlined
} from '@ant-design/icons';
import { Chart, Animate, usePermissions } from 'components/lib';
import { fetchSignupData, invalidateSignupDataCache } from '../store/slices/signupDataSlice';
import { getCacheKey } from '../store/cacheUtils';

const { Title, Text } = Typography;
const { Option } = Select;

// Distinct color palette for charts
const CHART_COLORS = [
  '#8B5CF6', '#10B981', '#3B82F6', '#F59E0B', '#EF4444',
  '#EC4899', '#14B8A6', '#6366F1', '#84CC16', '#F97316',
  '#06B6D4', '#8B4513', '#9333EA', '#059669', '#DC2626',
];

export function SignupPromotionalData() {
  const dispatch = useDispatch();
  const { can } = usePermissions();

  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    company_size: '',
    company_type: '',
    search: ''
  });

  const [searchInput, setSearchInput] = useState('');

  const signupDataState = useSelector((state) => state.signupData);
  const cacheKey = getCacheKey(filters);
  const data = signupDataState.data[cacheKey];
  const loading = signupDataState.loading;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput, page: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    dispatch(fetchSignupData(filters));
  }, [dispatch, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleRefresh = () => {
    console.log("INN")
    dispatch(invalidateSignupDataCache());
    dispatch(fetchSignupData(filters));
  };

  const summary = data?.summary || {};
  const promotionalInfo = data?.promotional_info || {};
  const analytics = promotionalInfo?.analytics || {};
  const items = promotionalInfo?.items || [];
  const pagination = promotionalInfo?.pagination || {};

  // Prepare chart data for Company Size
  const companySizeEntries = Object.entries(analytics?.company_size_distribution || {});
  const companySizeData = {
    labels: companySizeEntries.map(([label]) => label),
    datasets: [{
      label: 'Company Size',
      data: companySizeEntries.map(([, value]) => value),
      backgroundColor: companySizeEntries.map((_, idx) => CHART_COLORS[idx % CHART_COLORS.length]),
    }]
  };

  // Prepare chart data for Company Type
  const companyTypeEntries = Object.entries(analytics?.company_type_distribution || {});
  const companyTypeData = {
    labels: companyTypeEntries.map(([label]) => label),
    datasets: [{
      label: 'Company Type',
      data: companyTypeEntries.map(([, value]) => value),
      backgroundColor: companyTypeEntries.map((_, idx) => CHART_COLORS[idx % CHART_COLORS.length]),
    }]
  };

  const topFeatures = (analytics?.feature_popularity || []).slice(0, 10);

  // Feature Popularity Bar Chart
  const featureChartData = {
    labels: topFeatures.map(f => f.feature.replace(/_/g, ' ').toUpperCase()),
    datasets: [{
      label: 'Popularity',
      data: topFeatures.map(f => f.count),
      backgroundColor: '#8B5CF6',
    }]
  };

  // Table columns
  const columns = [
    {
      title: 'Company',
      dataIndex: 'company_name',
      key: 'company_name',
      render: (text) => <Text strong>{text || 'N/A'}</Text>,
    },
    {
      title: 'Admin',
      key: 'admin',
      render: (_, record) => (
        <div>
          <Text>{record.admin_name || 'N/A'}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>{record.admin_email}</Text>
        </div>
      ),
    },
    {
      title: 'Workspace',
      dataIndex: 'workspace_name',
      key: 'workspace_name',
      render: (text) => <Tag color="blue">{text || 'N/A'}</Tag>,
    },
    {
      title: 'Company Size',
      dataIndex: 'company_size',
      key: 'company_size',
      render: (text) => <Tag>{text || 'N/A'}</Tag>,
    },
    {
      title: 'Company Type',
      dataIndex: 'company_type',
      key: 'company_type',
      render: (text) => <Tag color="purple">{text || 'N/A'}</Tag>,
    },
    {
      title: 'Features',
      dataIndex: 'features_count',
      key: 'features_count',
      render: (count) => (
        <Tooltip title={`${count} features selected`}>
          <Tag color="green">{count}</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Previous HRM',
      dataIndex: 'used_hrm',
      key: 'used_hrm',
      render: (text) => text && text !== 'None' ? <Tag color="orange">{text}</Tag> : <Text type="secondary">None</Text>,
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      render: (text) => <Tag color="cyan">{text || 'Free'}</Tag>,
    },
    {
      title: 'Date',
      dataIndex: 'date_created',
      key: 'date_created',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
    },
    {
      ...can('signup_data', 'view') && {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => (
          <Space>
            {record.admin_email && can('signup_data', 'view') && (
              <Tooltip title="Send Email">
                <Button
                  type="text"
                  icon={<MailOutlined />}
                  onClick={() => window.location.href = `mailto:${record.admin_email}`}
                />
              </Tooltip>
            )}
          </Space>
        ),
      }
    },
  ];

  const tableData = items.map(item => ({
    key: item._id,
    company_name: item.company_name,
    admin_name: item.admin_name,
    admin_email: item.admin_email,
    workspace_name: item.workspace_name,
    company_size: item.company_size,
    company_type: item.type_of_company,
    features_count: item.feature_liked?.length || 0,
    used_hrm: item.used_hrm || 'None',
    date_created: item.date_created,
    plan: item.workspace_plan
  }));

  return (
    <Animate type="pop">
      <div>
        {/* Header */}
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <BarChartOutlined style={{ marginRight: 8 }} />
              Signup Promotional Data
            </Title>
            <Text type="secondary">Analyze company information and feature preferences from signups</Text>
          </div>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>Refresh</Button>
        </div>

        {/* Summary Stats */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Signups"
                value={summary.total_signups || 0}
                prefix={<UserAddOutlined style={{ color: '#8B5CF6' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Feedback Received"
                value={summary.total_feedback || 0}
                prefix={<MessageOutlined style={{ color: '#10B981' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Avg Rating"
                value={summary.avg_rating?.toFixed(1) || '0.0'}
                prefix={<StarOutlined style={{ color: '#F59E0B' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Top Feature"
                value={topFeatures[0]?.feature?.replace(/_/g, ' ') || 'N/A'}
                prefix={<TrophyOutlined style={{ color: '#EF4444' }} />}
                valueStyle={{ fontSize: 14 }}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Input
                placeholder="Search by company, admin, or workspace name..."
                prefix={<SearchOutlined />}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={8}>
              <Select
                placeholder="All Company Sizes"
                allowClear
                style={{ width: '100%' }}
                value={filters.company_size || undefined}
                onChange={(val) => handleFilterChange('company_size', val || '')}
              >
                {Object.keys(analytics?.company_size_distribution || {}).map(size => (
                  <Option key={size} value={size}>{size}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={8}>
              <Select
                placeholder="All Company Types"
                allowClear
                style={{ width: '100%' }}
                value={filters.company_type || undefined}
                onChange={(val) => handleFilterChange('company_type', val || '')}
              >
                {Object.keys(analytics?.company_type_distribution || {}).map(type => (
                  <Option key={type} value={type}>{type}</Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Card>

        {/* Distribution Charts */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={12}>
            <Card
              title={
                <Space>
                  <PieChartOutlined />
                  <Text strong>Company Size Distribution</Text>
                </Space>
              }
            >
              {companySizeEntries.length > 0 ? (
                <Chart type="donut" data={companySizeData} legend={true} />
              ) : (
                <Empty description="No data available" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card
              title={
                <Space>
                  <ShopOutlined />
                  <Text strong>Company Type Distribution</Text>
                </Space>
              }
            >
              {companyTypeEntries.length > 0 ? (
                <Chart type="pie" data={companyTypeData} legend={true} />
              ) : (
                <Empty description="No data available" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Card>
          </Col>
        </Row>

        {/* Feature Popularity */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={12}>
            <Card title={
              <Space>
                <TrophyOutlined />
                <Text strong>Top 10 Popular Features</Text>
              </Space>
            }>
              {topFeatures.length > 0 ? (
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  {topFeatures.map((feature, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px',
                        backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff',
                        borderRadius: 8,
                        marginBottom: 8,
                        transition: 'all 0.3s',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#fafafa' : '#ffffff'}
                    >
                      <Space>
                        <Text strong style={{ color: '#8B5CF6' }}>{index + 1}.</Text>
                        <Text>{feature.feature.replace(/_/g, ' ').toUpperCase()}</Text>
                      </Space>
                      <Tag color="purple">{feature.count}</Tag>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty description="No feature data available" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title={
              <Space>
                <BarChartOutlined />
                <Text strong>HRM Systems Used</Text>
              </Space>
            }>
              {analytics?.used_hrm_distribution ? (
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  {Object.entries(analytics.used_hrm_distribution)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 10)
                    .map(([hrm, count], index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px',
                          backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff',
                          borderRadius: 8,
                          marginBottom: 8,
                          transition: 'all 0.3s',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#fafafa' : '#ffffff'}
                      >
                        <Text>{hrm === 'null' ? 'Not Specified' : hrm}</Text>
                        <Tag color="orange">{count}</Tag>
                      </div>
                    ))}
                </div>
              ) : (
                <Empty description="No HRM data available" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Card>
          </Col>
        </Row>

        {/* Feature Bar Chart */}
        <Card
          title={
            <Space>
              <BarChartOutlined />
              <Text strong>Feature Popularity Chart</Text>
            </Space>
          }
          style={{ marginBottom: 16 }}
        >
          {topFeatures.length > 0 ? (
            <Chart type="bar" data={featureChartData} color="purple" />
          ) : (
            <Empty description="No data available" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Card>

        {/* Company Data Table */}
        <Card title={
          <Space>
            <ShopOutlined />
            <Text strong>Company Details</Text>
          </Space>
        }>
          <Table
            columns={columns}
            dataSource={tableData}
            loading={loading}
            pagination={{
              current: filters.page,
              pageSize: filters.limit,
              total: pagination.totalCount || 0,
              onChange: (page) => setFilters(prev => ({ ...prev, page })),
              showTotal: (total) => `Total ${total} signups`,
              showSizeChanger: true,
              pageSizeOptions: ['25', '50', '100'],
              onShowSizeChange: (_, size) => setFilters(prev => ({ ...prev, limit: size, page: 1 })),
            }}
            locale={{ emptyText: <Empty description="No signup data found" /> }}
            scroll={{ x: 1200 }}
          />
        </Card>
      </div>
    </Animate>
  );
}